// Slice 31 — BesideU Admin Governance tab (DESIGN §6.6).
//
// Single pane of glass for the platform-level governance items that
// unblock the pilot:
//   - "Of record" CGC member per discipline (BACP grief counsellor, MHP,
//     GP/psychiatrist, legal counsel)
//   - DPIA owner + DPIA scope text
//   - Testing programme owner
//   - Active DPA version (read-only here; authoring + activation happen
//     in /admin/prompts with mode=DPA via the standard CGC governance
//     workflow)
//
// Each role card has an inline editor that supports three assignment
// shapes: a CGC member from the existing roster, an external named
// person (name + email), or "unassigned". The DPIA scope is a free-text
// textarea. The DPA panel is read-only and links to the System Prompts
// tab with mode=DPA pre-filtered.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { StubSourceBadge } from '@/components/ui/StubSourceBadge.tsx';
import {
  AssignGovernanceRoleBodySchema,
  GOVERNANCE_ROLE_CGC_KIND_REQUIREMENT,
  type AdminCgcMemberSummary,
  type AssignGovernanceRoleBody,
  type ClassifierCategoryInspectorEntry,
  type ClassifierCategoryInspectorResponse,
  type GovernanceOverview,
  type GovernanceRole,
  type GovernanceRoleEntry,
  type GovernanceSettingEntry,
  type UpdateGovernanceSettingBody,
} from '@besideu/shared/admin';
import {
  CreateQaSampleSchema,
  QA_CALIBRATION_DEFAULT_MIN_SAMPLES,
  QA_SAMPLE_MODES,
  type CreateQaSampleBody,
  type LlmJudgeRollupResponse,
  type PendingRedactionListResponse,
  type QaRubricDimensionEntry,
  type QaRubricInspectorResponse,
  type QaRubricRollupResponse,
  type QaSampleMode,
  type QaSampleSummary,
} from '@besideu/shared/qa-rubric';
import { api, ApiError } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';

const ROLE_LABELS: Record<GovernanceRole, { title: string; description: string }> = {
  bacp_grief_counsellor: {
    title: 'BACP grief counsellor of record',
    description:
      'Approves Manager_Guidance prompts. Must be a BACP-registered grief counsellor (CGC kind: grief_counsellor).',
  },
  mhp: {
    title: 'Mental health professional of record',
    description:
      'Joint sign-off (with the GP/psychiatrist) for Employee_Companion + Trajectory_Classifier + Companion_* prompts. CGC kind: mhp.',
  },
  gp_psychiatrist: {
    title: 'GP / psychiatrist of record',
    description:
      'Joint sign-off (with the MHP) for Employee_Companion + Trajectory_Classifier + Companion_* prompts. CGC kind: gp_psychiatrist.',
  },
  legal_counsel: {
    title: 'Legal counsel of record',
    description:
      'Approves HR_Maeve, AI_Disclosure, Policy_Audit_*, Boilerplate_*, and DPA prompts. CGC kind: legal_counsel.',
  },
  dpia_owner: {
    title: 'DPIA owner',
    description:
      'Named owner of the Data Protection Impact Assessment. May be a CGC member (often legal counsel) or an external person (e.g. the engaging organisation’s data protection officer).',
  },
  testing_programme_owner: {
    title: 'Testing programme owner',
    description:
      'Named owner of the 100+ adversarial-scenario testing programme (Maeve Safety Guide Q9). May be a CGC member or an external clinical-research collaborator.',
  },
};

export function AdminGovernance() {
  const overview = useQuery({
    queryKey: ['admin', 'governance'],
    queryFn: () => api.get<GovernanceOverview>('/api/admin/governance'),
  });
  const cgcMembers = useQuery({
    queryKey: ['admin', 'cgc-members'],
    queryFn: () => api.get<{ members: AdminCgcMemberSummary[] }>('/api/admin/cgc-members'),
  });

  return (
    <section className="space-y-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Governance</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Platform-level allocations and settings that unblock the §5.3 governance items. Approval
          logic for prompts still gates on the CGC member’s <code>kind</code>; this surface records
          who fills each role for the deployment.
        </p>
      </header>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Role allocations</h2>
        {overview.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : overview.error || !overview.data ? (
          <p className="text-sm text-traffic-red" role="alert">
            Could not load governance overview.
          </p>
        ) : (
          <ul className="space-y-3">
            {overview.data.roles.map((entry) => (
              <RoleCard
                key={entry.role}
                entry={entry}
                cgcMembers={cgcMembers.data?.members ?? []}
                cgcMembersLoading={cgcMembers.isPending}
              />
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Free-text settings</h2>
        {overview.isPending ? null : overview.error || !overview.data ? null : (
          <ul className="space-y-3">
            {overview.data.settings.map((entry) => (
              <SettingCard key={entry.key} entry={entry} />
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Classifier categories</h2>
        <ClassifierCategoryInspector />
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">QA scoring rubric</h2>
        <QaRubricInspector />
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">QA samples</h2>
        <QaSamplesPanel />
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">LLM judge — population rollup</h2>
        <LlmJudgeRollupPanel />
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">QA redaction spot-check</h2>
        <PendingRedactionLink />
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Data Processing Agreement</h2>
        {overview.isPending ? null : overview.error || !overview.data ? null : (
          <GovernanceDocPanel
            doc={overview.data.dpa}
            title="Active DPA"
            mode="DPA"
            dbNote="A CGC-approved DPA row is live. Signup uses this wording + version."
            fallbackNote="No active DPA row in the database — signup falls back to the placeholder constant in @besideu/shared/signup. Author a draft via System Prompts and have legal counsel approve + activate it."
          />
        )}
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Data Protection Impact Assessment</h2>
        <p className="text-sm text-muted-foreground">
          A PROJECT §5 hard gate (#3.1). Sign-off is recorded through the standard CGC approve +
          activate workflow on a <code>DPIA</code>-mode prompt — audited and legal-counsel-gated,
          exactly like the DPA.
        </p>
        {overview.isPending ? null : overview.error || !overview.data ? null : (
          <GovernanceDocPanel
            doc={overview.data.dpia}
            title="Active DPIA"
            mode="DPIA"
            dbNote="A legal-counsel-approved DPIA row is live. This hard gate is satisfied."
            fallbackNote="No active DPIA row — the gate is OPEN. Author a draft via System Prompts and have legal counsel approve + activate it to close it."
          />
        )}
      </section>
    </section>
  );
}

// =============================================================================
// Role card — inline editor for a single role allocation.
// =============================================================================

function RoleCard({
  entry,
  cgcMembers,
  cgcMembersLoading,
}: {
  entry: GovernanceRoleEntry;
  cgcMembers: AdminCgcMemberSummary[];
  cgcMembersLoading: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const label = ROLE_LABELS[entry.role];

  return (
    <li className="rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-medium">{label.title}</p>
          <p className="mt-1 text-xs text-muted-foreground">{label.description}</p>
        </div>
        <Button variant="ghost" className="py-1 text-sm" onClick={() => setEditing((v) => !v)}>
          {editing ? 'Cancel' : entry.assignment.kind === 'unassigned' ? 'Assign' : 'Reassign'}
        </Button>
      </div>

      <div className="mt-3">
        <AssignmentDisplay entry={entry} />
      </div>

      {editing ? (
        <RoleEditor
          entry={entry}
          cgcMembers={cgcMembers}
          cgcMembersLoading={cgcMembersLoading}
          onDone={() => setEditing(false)}
        />
      ) : null}
    </li>
  );
}

function AssignmentDisplay({ entry }: { entry: GovernanceRoleEntry }) {
  const a = entry.assignment;
  if (a.kind === 'unassigned') {
    return (
      <p className="text-sm text-traffic-amber-text">
        <span className="font-medium">Unassigned</span> — no one is currently filling this role.
      </p>
    );
  }
  if (a.kind === 'cgc_member') {
    return (
      <div className="text-sm">
        <p className="font-medium">{a.cgcMemberName}</p>
        <p className="text-xs text-muted-foreground">
          {a.cgcMemberEmail} · CGC ({a.cgcMemberKind.replaceAll('_', ' ')})
        </p>
        {entry.assignedAt ? (
          <p className="mt-1 text-xs text-muted-foreground">
            Assigned {new Date(entry.assignedAt).toLocaleString('en-GB')}
            {entry.assignedByAdminName ? ` by ${entry.assignedByAdminName}` : ''}
          </p>
        ) : null}
      </div>
    );
  }
  // external
  return (
    <div className="text-sm">
      <p className="font-medium">{a.name}</p>
      <p className="text-xs text-muted-foreground">{a.email} · external</p>
      {entry.assignedAt ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Assigned {new Date(entry.assignedAt).toLocaleString('en-GB')}
          {entry.assignedByAdminName ? ` by ${entry.assignedByAdminName}` : ''}
        </p>
      ) : null}
    </div>
  );
}

function RoleEditor({
  entry,
  cgcMembers,
  cgcMembersLoading,
  onDone,
}: {
  entry: GovernanceRoleEntry;
  cgcMembers: AdminCgcMemberSummary[];
  cgcMembersLoading: boolean;
  onDone: () => void;
}) {
  const [kind, setKind] = useState<'cgc_member' | 'external' | 'unassigned'>(entry.assignment.kind);
  const [cgcMemberId, setCgcMemberId] = useState(
    entry.assignment.kind === 'cgc_member' ? entry.assignment.cgcMemberId : '',
  );
  const [name, setName] = useState(
    entry.assignment.kind === 'external' ? entry.assignment.name : '',
  );
  const [email, setEmail] = useState(
    entry.assignment.kind === 'external' ? entry.assignment.email : '',
  );
  const queryClient = useQueryClient();

  // Per-role kind requirement filters the picker — for `bacp_grief_counsellor`
  // only members with kind `grief_counsellor` are valid; `dpia_owner` and
  // `testing_programme_owner` accept any kind so the picker shows the
  // full active roster.
  const requiredKind = GOVERNANCE_ROLE_CGC_KIND_REQUIREMENT[entry.role];
  const filteredMembers = useMemo(
    () =>
      cgcMembers.filter(
        (m) => m.status === 'Active' && (requiredKind === null || m.kind === requiredKind),
      ),
    [cgcMembers, requiredKind],
  );

  const assign = useMutation({
    mutationFn: (body: AssignGovernanceRoleBody) =>
      api.put<{ ok: true }>(`/api/admin/governance/roles/${entry.role}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'governance'] });
      onDone();
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const body: AssignGovernanceRoleBody =
      kind === 'cgc_member'
        ? { kind: 'cgc_member', cgcMemberId }
        : kind === 'external'
          ? { kind: 'external', name: name.trim(), email: email.trim() }
          : { kind: 'unassigned' };
    const parsed = AssignGovernanceRoleBodySchema.safeParse(body);
    if (!parsed.success) {
      // The local form already filters invalid combinations, but
      // re-running the shared schema here surfaces any drift if the
      // shape ever changes.
      return;
    }
    assign.mutate(parsed.data);
  }

  return (
    <form
      className="mt-4 grid gap-3 rounded-md border border-dashed border-border bg-muted/20 p-4"
      onSubmit={submit}
    >
      <div role="radiogroup" aria-label="Assignment type" className="flex flex-wrap gap-3 text-sm">
        <label className="flex items-center gap-1.5">
          <input
            type="radio"
            checked={kind === 'cgc_member'}
            onChange={() => setKind('cgc_member')}
          />
          CGC member
        </label>
        <label className="flex items-center gap-1.5">
          <input type="radio" checked={kind === 'external'} onChange={() => setKind('external')} />
          External person
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="radio"
            checked={kind === 'unassigned'}
            onChange={() => setKind('unassigned')}
          />
          Unassign
        </label>
      </div>

      {kind === 'cgc_member' ? (
        <label className="block text-sm">
          <span className="text-xs tracking-wide text-muted-foreground uppercase">
            CGC member{requiredKind ? ` (kind: ${requiredKind.replaceAll('_', ' ')})` : ''}
          </span>
          <select
            className="mt-1 block w-full rounded-md border border-border bg-background px-2 py-2"
            value={cgcMemberId}
            onChange={(e) => setCgcMemberId(e.target.value)}
            required
          >
            <option value="" disabled>
              {cgcMembersLoading ? 'Loading members…' : 'Pick a member'}
            </option>
            {filteredMembers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.email})
              </option>
            ))}
          </select>
          {requiredKind && filteredMembers.length === 0 ? (
            <p className="mt-1 text-xs text-traffic-amber-text">
              No active CGC member with kind <code>{requiredKind}</code>. Invite one from the CGC
              Members tab first.
            </p>
          ) : null}
        </label>
      ) : kind === 'external' ? (
        <>
          <label className="block text-sm">
            <span className="text-xs tracking-wide text-muted-foreground uppercase">Name</span>
            <input
              type="text"
              required
              minLength={1}
              maxLength={120}
              className="mt-1 block w-full rounded-md border border-border bg-background px-2 py-2"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="text-xs tracking-wide text-muted-foreground uppercase">Email</span>
            <input
              type="email"
              required
              maxLength={254}
              className="mt-1 block w-full rounded-md border border-border bg-background px-2 py-2"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">
          Clears the current assignment. The role card stays visible as unassigned until you fill it
          again.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          type="submit"
          variant="primary"
          loading={assign.isPending}
          className="py-1.5 text-sm"
        >
          Save
        </Button>
        <Button type="button" variant="ghost" onClick={onDone} className="py-1.5 text-sm">
          Cancel
        </Button>
      </div>

      {assign.error ? (
        <p className="text-sm text-traffic-red" role="alert">
          {assign.error instanceof ApiError && assign.error.message === 'kind_mismatch'
            ? "That member's CGC kind doesn't match the role's required kind."
            : assign.error instanceof ApiError && assign.error.message === 'cgc_member_not_found'
              ? 'That member is no longer active. Refresh the page.'
              : 'Could not save. Try again.'}
        </p>
      ) : null}
    </form>
  );
}

// =============================================================================
// Setting card — inline edit for a free-text key (DPIA scope, etc.).
// =============================================================================

const SETTING_LABELS: Record<
  string,
  { title: string; description: string; rows: number; numeric?: boolean }
> = {
  dpia_scope: {
    title: 'DPIA scope',
    description:
      'Free-text description of the Data Protection Impact Assessment scope. Pre-pilot placeholder until the DPIA owner has authored the real scope (Maeve Safety Guide Q5).',
    rows: 8,
  },
  qa_calibration_min_samples: {
    title: 'QA calibration threshold',
    description: `Minimum human-scored opt-in samples per dimension before the QA judge is treated as calibrated for that dimension. Positive integer; defaults to ${QA_CALIBRATION_DEFAULT_MIN_SAMPLES} when unset.`,
    rows: 1,
    numeric: true,
  },
};

function SettingCard({ entry }: { entry: GovernanceSettingEntry }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.value ?? '');
  const queryClient = useQueryClient();
  const label = SETTING_LABELS[entry.key] ?? {
    title: entry.key,
    description: '',
    rows: 4,
  };

  const update = useMutation({
    mutationFn: (body: UpdateGovernanceSettingBody) =>
      api.put<{ ok: true }>(`/api/admin/governance/settings/${entry.key}`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'governance'] });
      // `qa_calibration_min_samples` lives in the LLM-judge rollup response
      // (minSamplesThreshold + every calibrated/drift flag + the n/threshold
      // badge), so the rollup must refetch too or it shows stale calibration
      // state after a threshold edit.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'qa-llm-rollup'] });
      setEditing(false);
    },
  });

  // Client-side guard for numeric settings: a canonical positive integer with
  // no leading zero. Mirrors the server's `String(n) === trimmed` check, so
  // "1e3", "1.0", "007", and "" are caught here with a clear message instead
  // of bouncing off an opaque 400.
  const numericInvalid = (label.numeric ?? false) && !/^[1-9]\d*$/.test(draft.trim());

  return (
    <li className="rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-medium">{label.title}</p>
          <p className="mt-1 text-xs text-muted-foreground">{label.description}</p>
        </div>
        <Button
          variant="ghost"
          className="py-1 text-sm"
          onClick={() => {
            if (!editing) setDraft(entry.value ?? '');
            setEditing((v) => !v);
          }}
        >
          {editing ? 'Cancel' : 'Edit'}
        </Button>
      </div>

      {editing ? (
        <form
          className="mt-3 grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (numericInvalid) return;
            update.mutate({ value: draft.trim() });
          }}
        >
          {label.numeric ? (
            <div className="grid gap-1">
              <input
                type="number"
                min={1}
                step={1}
                aria-invalid={numericInvalid}
                className="block w-32 rounded-md border border-border bg-background p-2 text-sm"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              {numericInvalid ? (
                <p className="text-xs text-traffic-red" role="alert">
                  Enter a whole number of 1 or more.
                </p>
              ) : null}
            </div>
          ) : (
            <textarea
              className="block w-full resize-y rounded-md border border-border bg-background p-3 text-sm"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={label.rows}
              maxLength={50_000}
            />
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              variant="primary"
              loading={update.isPending}
              disabled={numericInvalid}
              className="py-1.5 text-sm"
            >
              Save
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setEditing(false)}
              className="py-1.5 text-sm"
            >
              Cancel
            </Button>
          </div>
          {update.error ? (
            <p className="text-sm text-traffic-red" role="alert">
              Could not save. Try again.
            </p>
          ) : null}
        </form>
      ) : (
        <pre className="mt-3 max-h-60 overflow-y-auto rounded-md border border-border bg-muted/30 p-3 text-xs whitespace-pre-wrap">
          {entry.value ?? '(empty)'}
        </pre>
      )}

      {entry.updatedAt ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Updated {new Date(entry.updatedAt).toLocaleString('en-GB')}
          {entry.updatedByAdminName ? ` by ${entry.updatedByAdminName}` : ''}
        </p>
      ) : null}
    </li>
  );
}

// =============================================================================
// DPA panel — read-only here; authoring happens in System Prompts.
// =============================================================================

// =============================================================================
// Classifier category inspector — Slice 32. Read-only on the admin side;
// CGC dashboard has the parallel review-submit surface. One card per
// category showing the description, guardrail copy, routing kinds, and
// the per-version review status. A bump of CLASSIFIER_SET_VERSION in
// code invalidates all prior approvals — the inspector surfaces this
// as the version field at the section top.
// =============================================================================

function ClassifierCategoryInspector() {
  const inspector = useQuery({
    queryKey: ['admin', 'classifier-categories'],
    queryFn: () => api.get<ClassifierCategoryInspectorResponse>('/api/admin/classifier-categories'),
  });

  if (inspector.isPending) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (inspector.error || !inspector.data) {
    return (
      <p className="text-sm text-traffic-red" role="alert">
        Could not load classifier categories.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Eight CGC-candidate categories per DESIGN §5.3.1. Read-only here — CGC reviewers approve via
        the CGC dashboard. Current set version:{' '}
        <span className="font-mono">v{inspector.data.setVersion}</span>. A bump of this version in
        code invalidates all prior approvals.
      </p>
      <ul className="space-y-3">
        {inspector.data.categories.map((entry) => (
          <ClassifierCategoryCard key={entry.categoryKey} entry={entry} />
        ))}
      </ul>
    </div>
  );
}

function ClassifierCategoryCard({ entry }: { entry: ClassifierCategoryInspectorEntry }) {
  return (
    <li className="rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-medium">{entry.displayName}</p>
          <p className="text-xs text-muted-foreground">
            <span className="font-mono">{entry.categoryKey}</span> · routes to{' '}
            {entry.requiredKinds.map((k) => k.replaceAll('_', ' ')).join(' + ')}
            {entry.jointSignOff ? ' (joint sign-off)' : ' (single sign-off)'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StubSourceBadge source={entry.responseGuidanceSource} />
          <ReviewStatusBadge status={entry.status} />
        </div>
      </div>

      <div className="mt-3 space-y-2 text-sm">
        <div>
          <p className="text-xs tracking-wide text-muted-foreground uppercase">Description</p>
          <p className="mt-1 whitespace-pre-wrap">{entry.description}</p>
        </div>
        <div>
          <p className="text-xs tracking-wide text-muted-foreground uppercase">Guardrail copy</p>
          <p className="mt-1 whitespace-pre-wrap">{entry.guardrailCopy}</p>
        </div>
      </div>

      {entry.status.kind !== 'pending' ? (
        <div className="mt-3 border-t border-border pt-3">
          <p className="text-xs tracking-wide text-muted-foreground uppercase">Reviewers</p>
          <ul className="mt-1 space-y-1 text-xs">
            {entry.status.reviewers.map((r) => (
              <li key={r.cgcMemberId}>
                {r.cgcMemberName} ({r.cgcMemberKind.replaceAll('_', ' ')}) —{' '}
                {new Date(r.reviewedAt).toLocaleString('en-GB')}
                {r.notes ? ` · "${r.notes}"` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </li>
  );
}

function ReviewStatusBadge({ status }: { status: ClassifierCategoryInspectorEntry['status'] }) {
  if (status.kind === 'pending') {
    return (
      <span className="rounded-full bg-traffic-amber/10 px-2 py-0.5 text-[10px] font-medium tracking-normal text-traffic-amber-text uppercase">
        Pending review
      </span>
    );
  }
  if (status.kind === 'partially_reviewed') {
    return (
      <span
        className="rounded-full bg-traffic-amber/10 px-2 py-0.5 text-[10px] font-medium tracking-normal text-traffic-amber-text uppercase"
        title={`Awaiting: ${status.missingKinds.join(', ')}`}
      >
        Partially reviewed
      </span>
    );
  }
  return (
    <span className="rounded-full bg-traffic-green/10 px-2 py-0.5 text-[10px] font-medium tracking-normal text-traffic-green-text uppercase">
      Reviewed
    </span>
  );
}

// Read-only snapshot of a legal-text governance document sourced from a
// `system_prompts` row (DPA / DPIA mode) with code-constant fallback. Shared
// by the DPA + DPIA panels so the two can't drift.
function GovernanceDocPanel({
  doc,
  title,
  mode,
  dbNote,
  fallbackNote,
}: {
  doc: GovernanceOverview['dpa'];
  title: string;
  mode: 'DPA' | 'DPIA';
  dbNote: string;
  fallbackNote: string;
}) {
  return (
    <div className="rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-base font-medium">{title}</p>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-medium tracking-normal uppercase ${
            doc.source === 'db'
              ? 'bg-traffic-green/10 text-traffic-green-text'
              : 'bg-muted text-muted-foreground'
          }`}
        >
          {doc.source === 'db' ? `DB v${doc.version}` : `Constant fallback v${doc.version}`}
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {doc.source === 'db' ? dbNote : fallbackNote}
      </p>
      <pre className="mt-3 max-h-60 overflow-y-auto rounded-md border border-border bg-muted/30 p-3 text-xs whitespace-pre-wrap">
        {doc.body}
      </pre>
      <Link
        to={`/admin/prompts?mode=${mode}`}
        className="mt-3 inline-block text-sm text-primary underline-offset-2 hover:underline"
      >
        Manage {mode} versions in System Prompts →
      </Link>
    </div>
  );
}

// =============================================================================
// Slice 34 — QA scoring rubric inspector + samples panel.
//
// Mirrors the classifier inspector layout. Read-only for the per-dimension
// review status; admin observes which dimensions the CGC has signed off.
// Samples panel lets the admin paste in a redacted transcript + see the
// per-mode rollup of CGC scores.
// =============================================================================

const QA_MODE_LABELS: Record<QaSampleMode, string> = {
  Employee_Companion: 'Employee Companion',
  Manager_Guidance: 'Manager Guidance',
  HR_Maeve: 'HR Maeve',
};

function QaRubricInspector() {
  const inspector = useQuery({
    queryKey: ['admin', 'qa-rubric'],
    queryFn: () => api.get<QaRubricInspectorResponse>('/api/admin/qa-rubric'),
  });

  if (inspector.isPending) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  if (inspector.error || !inspector.data) {
    return (
      <p className="text-sm text-traffic-red" role="alert">
        Could not load QA scoring rubric.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Ten candidate dimensions per DESIGN §9.4 #5. CGC must sign off on each dimension before
        wider adviser engagement on samples begins. Read-only here — reviewers approve via the CGC
        dashboard. Current version: <span className="font-mono">v{inspector.data.version}</span>. A
        bump invalidates all prior approvals.
      </p>
      <ul className="space-y-3">
        {inspector.data.dimensions.map((entry) => (
          <QaRubricDimensionCard key={entry.dimensionKey} entry={entry} />
        ))}
      </ul>
    </div>
  );
}

function QaRubricDimensionCard({ entry }: { entry: QaRubricDimensionEntry }) {
  return (
    <li className="rounded-lg border border-border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-base font-medium">{entry.displayName}</p>
          <p className="text-xs text-muted-foreground">
            <span className="font-mono">{entry.dimensionKey}</span> · {entry.designRef} · routes to{' '}
            {entry.requiredKinds.map((k) => k.replaceAll('_', ' ')).join(' + ')}
            {entry.jointSignOff ? ' (joint sign-off)' : ' (single sign-off)'}
          </p>
        </div>
        <QaReviewStatusBadge status={entry.status} />
      </div>
      <p className="mt-3 text-sm whitespace-pre-wrap">{entry.description}</p>

      {entry.status.kind !== 'pending' ? (
        <div className="mt-3 border-t border-border pt-3">
          <p className="text-xs tracking-wide text-muted-foreground uppercase">Reviewers</p>
          <ul className="mt-1 space-y-1 text-xs">
            {entry.status.reviewers.map((r) => (
              <li key={r.cgcMemberId}>
                {r.cgcMemberName} ({r.cgcMemberKind.replaceAll('_', ' ')}) —{' '}
                {new Date(r.reviewedAt).toLocaleString('en-GB')}
                {r.notes ? ` · "${r.notes}"` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </li>
  );
}

function QaReviewStatusBadge({ status }: { status: QaRubricDimensionEntry['status'] }) {
  if (status.kind === 'pending') {
    return (
      <span className="rounded-full bg-traffic-amber/10 px-2 py-0.5 text-[10px] font-medium tracking-normal text-traffic-amber-text uppercase">
        Pending review
      </span>
    );
  }
  if (status.kind === 'partially_reviewed') {
    return (
      <span
        className="rounded-full bg-traffic-amber/10 px-2 py-0.5 text-[10px] font-medium tracking-normal text-traffic-amber-text uppercase"
        title={`Awaiting: ${status.missingKinds.join(', ')}`}
      >
        Partially reviewed
      </span>
    );
  }
  return (
    <span className="rounded-full bg-traffic-green/10 px-2 py-0.5 text-[10px] font-medium tracking-normal text-traffic-green-text uppercase">
      Reviewed
    </span>
  );
}

function QaSamplesPanel() {
  const samples = useQuery({
    queryKey: ['admin', 'qa-samples'],
    queryFn: () => api.get<{ samples: QaSampleSummary[] }>('/api/admin/qa-rubric/samples'),
  });
  const rollup = useQuery({
    queryKey: ['admin', 'qa-rollup'],
    queryFn: () => api.get<QaRubricRollupResponse>('/api/admin/qa-rubric/rollup'),
  });
  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Phase B (manual paste). Operator pastes a redacted excerpt; CGC scores each dimension 1–3 on
        the CGC dashboard. Cron-driven sampling depends on a separate retention-posture decision
        (Phase 1 §5.7.1 wipes session content at session end) — see future slice.
      </p>

      {rollup.data && rollup.data.averages.length > 0 ? (
        <QaRubricRollupTable rollup={rollup.data} />
      ) : null}

      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Samples</h3>
        <Button variant="ghost" className="py-1 text-sm" onClick={() => setCreating((v) => !v)}>
          {creating ? 'Cancel' : 'Create sample'}
        </Button>
      </div>

      {creating ? (
        <QaSampleCreator
          onDone={() => {
            setCreating(false);
          }}
        />
      ) : null}

      {samples.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : samples.error || !samples.data ? (
        <p className="text-sm text-traffic-red" role="alert">
          Could not load samples.
        </p>
      ) : samples.data.samples.length === 0 ? (
        <p className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          No samples yet. Create one above to start collecting CGC scores.
        </p>
      ) : (
        <ul className="space-y-2">
          {samples.data.samples.map((s) => (
            <QaSampleRow key={s.id} sample={s} />
          ))}
        </ul>
      )}
    </div>
  );
}

function QaRubricRollupTable({ rollup }: { rollup: QaRubricRollupResponse }) {
  type AverageEntry = QaRubricRollupResponse['averages'][number];
  const byMode = new Map<QaSampleMode, AverageEntry[]>();
  for (const a of rollup.averages) {
    const list = byMode.get(a.mode) ?? [];
    list.push(a);
    byMode.set(a.mode, list);
  }
  return (
    <div className="rounded-md border border-border bg-muted/20 p-4">
      <p className="text-xs tracking-wide text-muted-foreground uppercase">
        Per-mode rollup (rubric v{rollup.version})
      </p>
      <div className="mt-2 space-y-3">
        {[...byMode.entries()].map(([mode, rows]) => (
          <div key={mode}>
            <p className="text-sm font-medium">{QA_MODE_LABELS[mode]}</p>
            <ul className="mt-1 grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
              {rows.map((r) => (
                <li
                  key={`${r.mode}::${r.dimensionKey}`}
                  className="flex items-center justify-between gap-2 rounded border border-border bg-background px-2 py-1"
                >
                  <span className="truncate font-mono">{r.dimensionKey}</span>
                  <span className="text-muted-foreground">
                    {r.average.toFixed(2)} <span className="opacity-60">(n={r.sampleCount})</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// QA sampler redactor slice — badge + link to the spot-check queue.
// The full queue UI lives at /admin/qa-redaction; this panel just
// summarises the queue depth so the operator can see at a glance whether
// there's pending work without clicking through.
function PendingRedactionLink() {
  const list = useQuery({
    queryKey: ['admin', 'qa-pending-redaction'],
    queryFn: () => api.get<PendingRedactionListResponse>('/api/admin/qa-rubric/pending-redaction'),
    // Auto-refetch every 60s so the badge tracks the cron sweep without
    // forcing the operator to reload the page.
    refetchInterval: 60_000,
  });

  return (
    <div className="rounded-md border border-border bg-muted/20 p-4">
      <p className="text-xs text-muted-foreground">
        Opt-in Companion session transcripts queue here after the automated redactor flags them.
        Approve to promote to the CGC scoring pool, or reject if the draft can't be safely redacted.
        Each promotion is a click — no auto-promotion regardless of redactor confidence.
      </p>

      {list.isPending ? (
        <p className="mt-3 text-sm text-muted-foreground">Loading…</p>
      ) : list.error || !list.data ? (
        <p className="mt-3 text-sm text-traffic-red" role="alert">
          Could not load redaction queue.
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          {!list.data.redactorPromptApproved ? (
            <p className="rounded border border-dashed border-border bg-background px-3 py-2 text-xs text-muted-foreground">
              Awaiting CGC approval of the <span className="font-mono">QA_Redactor</span> prompt.
              Until then, snapshots stay in <span className="font-mono">pending_redaction</span>{' '}
              indefinitely; the next cron tick after approval will start clearing the queue.
            </p>
          ) : null}
          <p className="text-sm">
            <span className="font-medium">{list.data.counts.pendingAdminReview}</span> awaiting
            admin review ·{' '}
            <span className="text-muted-foreground">
              {list.data.counts.pendingRedaction} awaiting redactor
            </span>
          </p>
          <Link
            to="/admin/qa-redaction"
            className="inline-block text-sm font-medium text-primary underline-offset-2 hover:underline"
          >
            Open spot-check queue →
          </Link>
        </div>
      )}
    </div>
  );
}

// QA sampler foundation slice — LLM judge population rollup panel.
// Runs over `llm_judge_scores` rows from inline session-end scoring.
// Three empty states:
//   - Query failure (502/503)             → red error message.
//   - No active `QA_Judge` prompt         → operator-actionable copy
//                                            ("Awaiting CGC approval of
//                                            the QA_Judge prompt").
//   - Prompt active but no scores yet    → neutral "No sessions scored
//                                            yet" copy.
// Signed delta with an explicit leading sign so the direction of judge bias
// is unambiguous: "+0.40" = judge scores higher (more lenient) than humans,
// "−0.40" = harsher. The sign is chosen from the rounded magnitude (not the
// raw delta) so a sub-rounding negative like -0.004 shows "+0.00" rather than
// a misleading signed-zero "−0.00". Uses a true minus glyph for the style.
function formatSignedDelta(delta: number): string {
  const magnitude = Math.abs(delta).toFixed(2);
  return magnitude === '0.00' ? `+${magnitude}` : delta < 0 ? `−${magnitude}` : `+${magnitude}`;
}

function LlmJudgeRollupPanel() {
  const rollup = useQuery({
    queryKey: ['admin', 'qa-llm-rollup'],
    queryFn: () => api.get<LlmJudgeRollupResponse>('/api/admin/qa-rubric/llm-rollup'),
  });

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Automated scoring runs at Companion session end against the same 10 dimensions a CGC member
        scores manually above. Each dimension stays{' '}
        <span className="font-medium">uncalibrated</span> until enough opt-in sessions have been
        human-scored; once calibrated, the signed Δ (judge minus human) shows how the judge differs
        from CGC scoring on the same sessions, with an amber drift flag when the gap is large enough
        to warrant a §5.5 prompt review.
      </p>

      {rollup.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rollup.error || !rollup.data ? (
        <p className="text-sm text-traffic-red" role="alert">
          Could not load LLM judge rollup.
        </p>
      ) : rollup.data.averages.length > 0 ? (
        // Render accrued data whenever it exists — even if the QA_Judge prompt
        // is no longer active (e.g. deactivated to author a revision). Hiding
        // it behind the "awaiting approval" placeholder would bury the very
        // calibration/drift signal that motivates a §5.5 prompt review.
        <>
          {!rollup.data.judgePromptModeApproved ? (
            <p className="rounded-md border border-traffic-amber/40 bg-traffic-amber/10 p-3 text-xs text-foreground">
              The <span className="font-mono">QA_Judge</span> prompt is not currently active, so no
              new sessions are being scored. The rollup below reflects sessions scored while a prior
              version was active.
            </p>
          ) : null}
          <LlmJudgeRollupTable rollup={rollup.data} />
        </>
      ) : !rollup.data.judgePromptModeApproved ? (
        <p className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          Awaiting CGC approval of the <span className="font-mono">QA_Judge</span> prompt. Once
          approved + activated, the next Companion session-end will start populating this rollup.
        </p>
      ) : (
        <p className="rounded-md border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          No sessions scored yet at rubric v{rollup.data.rubricVersion}. The next user-ended
          Companion session will populate this rollup.
        </p>
      )}
    </div>
  );
}

function LlmJudgeRollupTable({ rollup }: { rollup: LlmJudgeRollupResponse }) {
  type AverageEntry = LlmJudgeRollupResponse['averages'][number];
  const byMode = new Map<QaSampleMode, AverageEntry[]>();
  for (const a of rollup.averages) {
    const list = byMode.get(a.mode) ?? [];
    list.push(a);
    byMode.set(a.mode, list);
  }
  return (
    <div className="rounded-md border border-border bg-muted/20 p-4">
      <p className="text-xs tracking-wide text-muted-foreground uppercase">
        Per-mode rollup (rubric v{rollup.rubricVersion})
      </p>
      <div className="mt-2 space-y-3">
        {[...byMode.entries()].map(([mode, rows]) => (
          <div key={mode}>
            <p className="text-sm font-medium">{QA_MODE_LABELS[mode]}</p>
            <ul className="mt-1 grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
              {rows.map((r) => (
                <li
                  key={`${r.mode}::${r.dimensionKey}`}
                  className="flex items-center justify-between gap-2 rounded border border-border bg-background px-2 py-1"
                >
                  <span className="truncate font-mono">{r.dimensionKey}</span>
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <span>
                      {r.average.toFixed(2)} <span className="opacity-60">(n={r.sampleCount})</span>
                    </span>
                    {r.calibrated && r.calibration ? (
                      <>
                        <span
                          className="font-medium text-foreground"
                          title={`Judge ${r.calibration.llmSubsetAverage.toFixed(2)} vs human ${r.calibration.humanAverage.toFixed(2)} on ${r.calibration.pairedSampleCount} paired sample(s)`}
                        >
                          Δ {formatSignedDelta(r.calibration.delta)}
                        </span>
                        {r.calibration.drift ? (
                          <span
                            className="rounded bg-traffic-amber/15 px-1.5 py-0.5 text-[10px] font-medium text-traffic-amber-text"
                            title="Drift: judge–human gap exceeds tolerance. Flag for §5.5 prompt review."
                          >
                            drift
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <span
                        className="rounded bg-traffic-amber/15 px-1.5 py-0.5 text-[10px] font-medium text-traffic-amber-text"
                        title="Uncalibrated: not yet enough human-scored opt-in samples to compare the judge against CGC scoring on this dimension."
                      >
                        uncalibrated {r.calibration?.pairedSampleCount ?? 0}/
                        {rollup.minSamplesThreshold}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function QaSampleRow({ sample }: { sample: QaSampleSummary }) {
  return (
    <li className="rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{sample.sourceLabel}</p>
          <p className="text-xs text-muted-foreground">
            {QA_MODE_LABELS[sample.mode]} · created{' '}
            {new Date(sample.createdAt).toLocaleString('en-GB')}
            {sample.createdByAdminName ? ` by ${sample.createdByAdminName}` : ''}
          </p>
        </div>
        <div className="text-xs text-muted-foreground">
          {sample.dimensionsScoredCount} / 10 dimensions scored · {sample.scoreRowCount} total score
          rows
        </div>
      </div>
    </li>
  );
}

function QaSampleCreator({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<QaSampleMode>('Employee_Companion');
  const [sourceLabel, setSourceLabel] = useState('');
  const [redactedTranscript, setRedactedTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: (body: CreateQaSampleBody) =>
      api.post<{ ok: true; id: string }>('/api/admin/qa-rubric/samples', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'qa-samples'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'qa-rollup'] });
      onDone();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Create failed.'),
  });

  return (
    <form
      className="space-y-3 rounded-lg border border-border bg-muted/20 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        const parsed = CreateQaSampleSchema.safeParse({ mode, sourceLabel, redactedTranscript });
        if (!parsed.success) {
          setError(parsed.error.issues[0]?.message ?? 'Invalid input.');
          return;
        }
        createMutation.mutate(parsed.data);
      }}
    >
      <p className="text-xs text-muted-foreground">
        Operator-redacted text only. Do not paste PII, names, employer identifiers, or any Article 9
        special category details. Storage is metadata-class within besideU and audit- logged.
      </p>
      <div>
        <label className="block text-xs font-medium text-muted-foreground" htmlFor="qa-mode">
          Mode
        </label>
        <select
          id="qa-mode"
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={mode}
          onChange={(e) => setMode(e.target.value as QaSampleMode)}
        >
          {QA_SAMPLE_MODES.map((m) => (
            <option key={m} value={m}>
              {QA_MODE_LABELS[m]}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs font-medium text-muted-foreground" htmlFor="qa-label">
          Source label
        </label>
        <input
          id="qa-label"
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          value={sourceLabel}
          onChange={(e) => setSourceLabel(e.target.value)}
          maxLength={200}
          placeholder='e.g. "synthetic adversarial #12" or "redacted excerpt 2026-05-20"'
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-muted-foreground" htmlFor="qa-transcript">
          Redacted transcript
        </label>
        <textarea
          id="qa-transcript"
          className="mt-1 h-40 w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
          value={redactedTranscript}
          onChange={(e) => setRedactedTranscript(e.target.value)}
          maxLength={50000}
        />
      </div>
      {error ? (
        <p className="text-xs text-traffic-red" role="alert">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" type="button" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={createMutation.isPending}>
          {createMutation.isPending ? 'Creating…' : 'Create sample'}
        </Button>
      </div>
    </form>
  );
}
